import {
  CustomerScreen,
  ProcessSessionType,
  ProcessStep,
  type CreateProcessSessionDto,
  type PatchProcessSessionDto,
  type ProcessSessionGatesDto,
  type ProcessSessionResponseDto,
} from '@carvia/ros-client-types';
import {
  useCreateProcessSession,
  useDeleteProcessSession,
  usePatchProcessSession,
  useProcessSession,
} from '@/queries/use-bookings';
import { useProcessSessionEvents } from '@/queries/use-process-session';
import { useTokenPayload } from '@/queries/auth/use-login';
import { Alert } from '@/services/alert.service';
import { i18n } from '@/i18n';
import { createToast } from 'mosha-vue-toastify';
import { isAxiosError } from 'axios';
import { getCounterInUseDetails, isCounterInUseError } from '@/utils/process-session-errors';
import {
  computed,
  inject,
  provide,
  ref,
  watch,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
  type Ref,
  toValue,
} from 'vue';
import {
  useRoute,
  useRouter,
  onBeforeRouteLeave,
  type RouteLocationAsRelativeGeneric,
  type Router,
} from 'vue-router';
import type { Booking } from '@/entities/bookings/booking.entity';
import { resolveCustomerScreen } from '@/utils/resolve-customer-screen';
import { useAbility } from '@casl/vue';
import type { RouterMeta } from '@/router';

function isSessionRevisionMismatch(error: unknown): boolean {
  if (!isAxiosError(error) || error.response?.status !== 409) return false;
  return !isCounterInUseError(error);
}

function getListRouteWithQuery(
  router: Router,
  listRouteName: string,
): RouteLocationAsRelativeGeneric {
  const route = { name: listRouteName };
  const back = router.options.history.state.back;
  if (typeof back !== 'string') {
    return route;
  }

  const resolved = router.resolve(back);
  if (resolved.name !== listRouteName) {
    return route;
  }

  return { ...route, query: resolved.query };
}

export const LAST_COUNTER_ID_KEY = 'ros:last-counter-id';

export interface ProcessStepDefinition {
  processStep: ProcessStep | null;
  routeName: string;
  routeParams?: Record<string, string>;
}

export interface ProcessNavigationContext {
  goToNext: () => Promise<void>;
  goToPrevious: () => Promise<void>;
  currentStepIndex: ComputedRef<number>;
  isFirstStep: ComputedRef<boolean>;
  isLastStep: ComputedRef<boolean>;
  steps: ComputedRef<ProcessStepDefinition[]>;
}

export const PROCESS_NAVIGATION_KEY: InjectionKey<ProcessNavigationContext> =
  Symbol('process-navigation');

export interface ProcessSessionContext {
  session: ComputedRef<ProcessSessionResponseDto | null | undefined>;
  isCounterHandover: ComputedRef<boolean>;
  isLoadingSession: ComputedRef<boolean>;
  patchSessionFields: (fields: Omit<PatchProcessSessionDto, 'revision'>) => Promise<void>;
  patchCounterId: (counterId: string | null) => Promise<void>;
}

export const PROCESS_SESSION_KEY: InjectionKey<ProcessSessionContext> = Symbol('process-session');

export function useProcessNavigation(): ProcessNavigationContext {
  const context = inject(PROCESS_NAVIGATION_KEY);
  if (!context) {
    throw new Error('useProcessNavigation must be used within a process view');
  }
  return context;
}

export function useProcessSessionContext(): ProcessSessionContext {
  const context = inject(PROCESS_SESSION_KEY);
  if (!context) {
    throw new Error('useProcessSessionContext must be used within a process view');
  }
  return context;
}

export function isHandoverStep(step: ProcessStep | null): boolean {
  return step?.startsWith('HANDOVER_') ?? false;
}

export function isReturnStep(step: ProcessStep | null): boolean {
  return step?.startsWith('RETURN_') ?? false;
}

const PROCESS_STEP_TO_ROUTE: Record<ProcessStep, string> = {
  [ProcessStep.HANDOVER_OVERVIEW]: 'handoverDetail',
  [ProcessStep.HANDOVER_SURVEY]: 'handoverSurvey',
  [ProcessStep.HANDOVER_CLIENT_DATA]: 'handoverClientData',
  [ProcessStep.HANDOVER_PAYMENT]: 'handoverPayment',
  [ProcessStep.HANDOVER_CAR_STATUS]: 'handoverCarStatus',
  [ProcessStep.HANDOVER_INSPECTION]: 'handoverInspection',
  [ProcessStep.HANDOVER_CONTRACT]: 'handoverRentalContract',
  [ProcessStep.RETURN_OVERVIEW]: 'returnOverview',
  [ProcessStep.RETURN_DAMAGES]: 'returnDamages',
  [ProcessStep.RETURN_CAR_STATUS]: 'returnCarStatus',
  [ProcessStep.RETURN_INSPECTION]: 'returnInspection',
};

export function getRouteForProcessStep(step: ProcessStep): string | null {
  return PROCESS_STEP_TO_ROUTE[step] ?? null;
}

function currentRouteProcessStep(
  steps: ProcessStepDefinition[],
  currentStepIndex: number,
): ProcessStep | null {
  if (currentStepIndex === -1) return null;
  return steps[currentStepIndex]?.processStep ?? null;
}

interface UseProcessSessionTrackingOptions {
  booking: Ref<Booking | undefined>;
  steps: ComputedRef<ProcessStepDefinition[]>;
  listRouteName: string;
  stepsReady: ComputedRef<boolean>;
  sessionType: ProcessSessionType;
  resolveCreatePayload?: () => CreateProcessSessionDto;
}

export function useProcessSessionTracking({
  booking,
  steps,
  listRouteName,
  stepsReady,
  sessionType,
  resolveCreatePayload,
}: UseProcessSessionTrackingOptions) {
  const route = useRoute();
  const router = useRouter();
  const { t } = i18n.global;

  function translateSessionType(type: ProcessSessionType): string {
    return t(`processSession.sessionTypes.${type}`);
  }
  const listRouteLocation = getListRouteWithQuery(router, listRouteName);
  // Agents and External drivers alike own a session by their token principal id.
  const { data: tokenPayload } = useTokenPayload();
  const me = computed(() => (tokenPayload.value ? { id: tokenPayload.value.id } : undefined));

  const bookingId = computed(() => booking.value?.id);
  const sessionQueryEnabled = computed(() => !!bookingId.value && stepsReady.value);

  const {
    data: sessionData,
    isLoading: isLoadingSession,
    refetch: refetchSession,
  } = useProcessSession(bookingId, sessionQueryEnabled);
  const { mutateAsync: createSession } = useCreateProcessSession();
  const { mutateAsync: patchSession } = usePatchProcessSession();
  const { mutateAsync: deleteSession } = useDeleteProcessSession();

  const session = computed(() => sessionData.value ?? null);
  const agentProcessStep = computed(() => session.value?.agentProcessStep ?? null);
  const isCounterHandover = computed(() => !!session.value?.counterId);

  const currentStepIndex = computed(() =>
    steps.value.findIndex((step) => step.routeName === route.name),
  );
  const isFirstStep = computed(() => currentStepIndex.value === 0);
  const isLastStep = computed(() => currentStepIndex.value === steps.value.length - 1);

  const forwardCheckDone = ref(false);
  const navigationComplete = ref(false);
  const sessionInitComplete = ref(false);
  const sessionEstablished = ref(false);
  const isNavigating = ref(false);
  const isResumingRedirect = ref(false);
  const preserveSessionOnLeave = ref(false);
  let patchChain = Promise.resolve();
  let pendingPatchFields: Omit<PatchProcessSessionDto, 'revision'> | null = null;
  let sessionInitInFlight: Promise<void> | null = null;
  let createSessionInFlight: Promise<ProcessSessionResponseDto> | null = null;

  async function navigateToList(options?: { preserveSession?: boolean }) {
    preserveSessionOnLeave.value = options?.preserveSession ?? false;
    await router.push(listRouteLocation).finally(() => {
      preserveSessionOnLeave.value = false;
    });
  }

  const ability = useAbility();
  const canOpenList = computed(() => {
    const { requireAuth } = router.resolve({ name: listRouteName }).meta as RouterMeta;
    return !Array.isArray(requireAuth) || requireAuth.every((rule) => ability.can(...rule));
  });
  // Without a list to return to (External driver), a session someone else holds pauses the process.
  // Derived from the session itself so the step sync never claims it back before the pause shows.
  const isPaused = computed(
    () =>
      !canOpenList.value &&
      !!me.value?.id &&
      !!session.value?.agentUserId &&
      session.value.agentUserId !== me.value.id,
  );

  const handleTakenOver = () => {
    void refetchSession().then((result) => {
      const updated = result.data;
      if (updated && me.value?.id && updated.agentUserId !== me.value.id && canOpenList.value) {
        createToast(takenOverMessage(), { type: 'warning' });
        void navigateToList({ preserveSession: true });
      }
    });
  };

  const handleCounterReleased = () => {
    void refetchSession().then(() => {
      createToast(counterReleasedMessage(), { type: 'warning' });
    });
  };

  useProcessSessionEvents(
    bookingId,
    computed(() => sessionEstablished.value && !!bookingId.value),
    handleTakenOver,
    handleCounterReleased,
  );

  function takenOverMessage(): string {
    return t('processSession.takenOver');
  }

  function counterReleasedMessage(): string {
    return t('processSession.counterReleased');
  }

  function promptCounterTakeover(_counterId: string): Promise<boolean> {
    return Alert.fire({
      title: t('processSession.counterInUseTitle'),
      text: t('processSession.counterInUseText'),
      icon: 'warning',
      buttons: [
        { propertyName: 'takeOver', text: t('processSession.takeOverCounter'), style: 'danger' },
        { propertyName: 'cancel', text: t('cancel'), style: 'ghost' },
      ],
    }).then((result) => !!result.takeOver);
  }

  async function createSessionWithCounterHandling(
    dto: CreateProcessSessionDto,
  ): Promise<ProcessSessionResponseDto> {
    if (!bookingId.value) {
      throw new Error('Missing booking id');
    }

    try {
      return await createSession({ bookingId: bookingId.value, dto });
    } catch (error) {
      if (!isCounterInUseError(error) || !dto.counterId) {
        throw error;
      }

      const details = getCounterInUseDetails(error);
      if (await promptCounterTakeover(details?.counterId ?? dto.counterId)) {
        return createSession({
          bookingId: bookingId.value,
          dto: { ...dto, forceCounterTakeover: true },
        });
      }

      return createSession({
        bookingId: bookingId.value,
        dto: { ...dto, counterId: null },
      });
    }
  }

  async function ensureSession(): Promise<{
    session: ProcessSessionResponseDto;
    created: boolean;
  } | null> {
    if (!bookingId.value) return null;

    let current = sessionData.value;
    if (current === undefined && !isLoadingSession.value) {
      current = (await refetchSession()).data ?? null;
    }
    while (isLoadingSession.value) {
      await new Promise((resolve) => setTimeout(resolve, 50));
      current = sessionData.value ?? null;
    }

    let created = false;

    if (current) {
      if (current.type !== sessionType) {
        const result = await Alert.fire<string, 'continue' | 'cancel'>({
          title: t('processSession.processAlreadyInProgressTitle'),
          text: t('processSession.processAlreadyInProgressText', {
            activeType: translateSessionType(current.type),
            sessionType: translateSessionType(sessionType),
          }),
          icon: 'warning',
          buttons: [
            { propertyName: 'continue', text: t('continue'), style: 'danger' },
            { propertyName: 'cancel', text: t('cancel'), style: 'ghost' },
          ],
        });
        if (result.cancel) {
          await navigateToList({ preserveSession: true });
          return null;
        }
        await deleteSession(bookingId.value);
        current = null;
      } else if (
        me.value?.id &&
        current.agentUserId &&
        current.agentUserId !== me.value.id &&
        canOpenList.value
      ) {
        const result = await Alert.fire<string, 'continue' | 'cancel'>({
          title: t('processSession.processInProgressTitle'),
          text: t('processSession.processInProgressText'),
          icon: 'warning',
          buttons: [
            { propertyName: 'continue', text: t('continue'), style: 'default' },
            { propertyName: 'cancel', text: t('cancel'), style: 'ghost' },
          ],
        });
        if (result.cancel) {
          await navigateToList({ preserveSession: true });
          return null;
        }
      }
    }

    if (!current) {
      const dto = resolveCreatePayload?.() ?? { type: sessionType };
      if (!createSessionInFlight) {
        createSessionInFlight = createSessionWithCounterHandling(dto).finally(() => {
          createSessionInFlight = null;
        });
      }
      current = await createSessionInFlight;
      created = true;
    }

    sessionEstablished.value = true;
    return { session: current, created };
  }

  async function redirectToStep(stepDef: ProcessStepDefinition | undefined) {
    if (!stepDef || !booking.value?.id) return;
    if (route.name === stepDef.routeName) return;

    isResumingRedirect.value = true;
    try {
      await router.replace({
        name: stepDef.routeName,
        params: { id: booking.value.id, ...stepDef.routeParams },
      });
    } finally {
      isResumingRedirect.value = false;
    }
  }

  watch(
    [booking, stepsReady, isLoadingSession, sessionData],
    async () => {
      if (
        sessionInitComplete.value ||
        !booking.value ||
        !stepsReady.value ||
        isLoadingSession.value
      ) {
        return;
      }

      if (sessionInitInFlight) {
        await sessionInitInFlight;
        return;
      }

      sessionInitInFlight = (async () => {
        const established = await ensureSession();
        if (!established) return;

        sessionInitComplete.value = true;

        const firstStep = steps.value[0];
        if (!firstStep) {
          navigationComplete.value = true;
          return;
        }

        if (forwardCheckDone.value) {
          navigationComplete.value = true;
          return;
        }

        forwardCheckDone.value = true;

        if (established.created) {
          // Only normalize route when still on (or before) the first step. If the user already
          // advanced while session creation was in flight, do not pull them back to the start.
          if (currentStepIndex.value <= 0) {
            await redirectToStep(firstStep);
          }
          navigationComplete.value = true;
          return;
        }

        const storedStep = established.session.agentProcessStep;
        if (storedStep && route.name === firstStep.routeName) {
          const targetStep = steps.value.find((s) => s.processStep === storedStep);
          if (targetStep && targetStep.routeName !== firstStep.routeName) {
            await redirectToStep(targetStep);
          }
        }
        navigationComplete.value = true;
      })();

      try {
        await sessionInitInFlight;
      } finally {
        sessionInitInFlight = null;
      }
    },
    { immediate: true },
  );

  async function patchSessionFields(fields: Omit<PatchProcessSessionDto, 'revision'>) {
    pendingPatchFields = { ...pendingPatchFields, ...fields };

    const flush = async () => {
      let outerAttempts = 0;

      while (pendingPatchFields) {
        if (outerAttempts++ > 5) {
          throw new Error('Failed to sync process session after multiple retries');
        }

        const fieldsToSend = pendingPatchFields;
        pendingPatchFields = null;

        if (!bookingId.value) return;

        const maxRevisionRetries = 8;
        let patched = false;
        let forceRefetch = false;

        for (let attempt = 0; attempt < maxRevisionRetries; attempt++) {
          let current = !forceRefetch ? (sessionData.value ?? null) : null;
          forceRefetch = false;
          if (!current) {
            current = (await refetchSession()).data ?? null;
          }
          if (!current) return;

          try {
            await patchSession({
              bookingId: bookingId.value,
              dto: { ...fieldsToSend, revision: current.revision },
            });
            patched = true;
            break;
          } catch (error) {
            if (!isSessionRevisionMismatch(error)) {
              throw error;
            }
            if (attempt === maxRevisionRetries - 1) {
              await refetchSession();
              pendingPatchFields = { ...fieldsToSend, ...(pendingPatchFields ?? {}) };
              break;
            }
            forceRefetch = true;
            await refetchSession();
          }
        }

        if (!patched && !pendingPatchFields) {
          throw new Error('Failed to sync process session');
        }
      }
    };

    const next = patchChain.then(flush);
    patchChain = next.catch(() => undefined);
    await next;
  }

  async function patchCounterId(counterId: string | null) {
    const persistCounterPreference = () => {
      if (counterId) {
        localStorage.setItem(LAST_COUNTER_ID_KEY, counterId);
      }
    };

    const step = currentRouteProcessStep(steps.value, currentStepIndex.value);
    const customerScreen = resolveCustomerScreen(step, counterId, session.value?.gates);
    const counterFields: Omit<PatchProcessSessionDto, 'revision'> = { counterId };
    if (step !== null && step !== session.value?.agentProcessStep) {
      counterFields.agentProcessStep = step;
    }
    if (customerScreen !== undefined) {
      counterFields.customerScreen = customerScreen;
    }

    try {
      await patchSessionFields(counterFields);
      persistCounterPreference();
    } catch (error) {
      if (!isCounterInUseError(error) || !counterId) {
        throw error;
      }

      const details = getCounterInUseDetails(error);
      if (await promptCounterTakeover(details?.counterId ?? counterId)) {
        await patchSessionFields({ ...counterFields, forceCounterTakeover: true });
        persistCounterPreference();
        return;
      }

      await refetchSession();
      throw error;
    }
  }

  watch(
    [
      () => route.name,
      bookingId,
      agentProcessStep,
      navigationComplete,
      () => session.value?.counterId,
      () => session.value?.customerScreen,
      () => session.value?.gates?.customerContactConfirmed,
      () => session.value?.gates?.customerSignatureCaptured,
      isResumingRedirect,
      isPaused,
    ],
    () => {
      if (!bookingId.value || !navigationComplete.value || !sessionInitComplete.value) return;
      if (isResumingRedirect.value || isPaused.value) return;

      const step =
        currentStepIndex.value === -1 ? null : steps.value[currentStepIndex.value].processStep;

      const customerScreen = resolveCustomerScreen(
        step,
        session.value?.counterId,
        session.value?.gates,
      );

      const patch: Omit<PatchProcessSessionDto, 'revision'> = {};
      let needsPatch = false;

      if (step !== agentProcessStep.value) {
        patch.agentProcessStep = step;
        needsPatch = true;
      }

      if (
        customerScreen !== undefined &&
        customerScreen !== session.value?.customerScreen &&
        session.value?.counterId
      ) {
        patch.customerScreen = customerScreen;
        needsPatch = true;
      }

      if (needsPatch && session.value) {
        void patchSessionFields(patch);
      }
    },
    { immediate: true },
  );

  async function goToNext() {
    if (!booking.value?.id) return;
    if (isNavigating.value) return;
    const nextIndex = currentStepIndex.value + 1;
    if (nextIndex >= steps.value.length) return;
    const nextStep = steps.value[nextIndex];
    isNavigating.value = true;
    try {
      await router.push({
        name: nextStep.routeName,
        params: { id: booking.value.id, ...nextStep.routeParams },
      });
    } finally {
      isNavigating.value = false;
    }
  }

  async function goToPrevious() {
    if (!booking.value?.id) return;
    if (isNavigating.value) return;
    isNavigating.value = true;
    try {
      const prevIndex = currentStepIndex.value - 1;
      if (prevIndex < 0) {
        await navigateToList();
        return;
      }
      const prevStep = steps.value[prevIndex];
      await router.push({
        name: prevStep.routeName,
        params: { id: booking.value.id, ...prevStep.routeParams },
      });
    } finally {
      isNavigating.value = false;
    }
  }

  /** Takes the session back from whoever paused it; patching claims it for this principal. */
  async function resume() {
    await patchSessionFields({
      agentProcessStep: currentRouteProcessStep(steps.value, currentStepIndex.value),
    });
  }

  async function clearProcessSession() {
    if (!bookingId.value) return;
    navigationComplete.value = false;
    forwardCheckDone.value = true;
    sessionInitComplete.value = true;
    await deleteSession(bookingId.value);
    sessionEstablished.value = false;
  }

  onBeforeRouteLeave(async () => {
    if (preserveSessionOnLeave.value) return;
    if (!sessionEstablished.value || !bookingId.value) return;
    if (!isFirstStep.value) return;

    await clearProcessSession();
  });

  const canGoBack = computed(() => !isFirstStep.value || canOpenList.value);

  const backRoute = computed(() => {
    const prevIndex = currentStepIndex.value - 1;
    if (!booking.value?.id || prevIndex < 0) {
      return listRouteLocation;
    }
    const prevStep = steps.value[prevIndex];
    return {
      name: prevStep.routeName,
      params: { id: booking.value.id, ...prevStep.routeParams },
    };
  });

  provide(PROCESS_NAVIGATION_KEY, {
    goToNext,
    goToPrevious,
    currentStepIndex,
    isFirstStep,
    isLastStep,
    steps,
  });

  provide(PROCESS_SESSION_KEY, {
    session,
    isCounterHandover,
    isLoadingSession: computed(() => isLoadingSession.value || !sessionInitComplete.value),
    patchSessionFields,
    patchCounterId,
  });

  return {
    goToNext,
    goToPrevious,
    clearProcessSession,
    backRoute,
    canGoBack,
    navigateToList,
    isLastStep,
    session,
    isCounterHandover,
    isPaused,
    resume,
    patchSessionFields,
    patchCounterId,
  };
}
